/**
 * The severity contract for entries in `file_imports.errors`.
 *
 * `file_imports` has ONE JSONB column for issues (`errors`) and no severity
 * field, so every importer note — blocking or not — lands in the same array.
 * The only marker distinguishing them is a literal prefix on the message, and
 * before DCP-359 that prefix existed twice with nothing linking the copies: a
 * template-string literal in the producers (`@dcp/tasks`) and a private
 * `const WARNING_PREFIX` in the consumer (`FileImportSection.tsx`). Nothing
 * failed if one side changed. This module is that missing link.
 *
 * Two classes, deliberately:
 *
 *  - PREFIXED (`WARNING: …`) — notes about the tenant's CATALOG (duplicate
 *    bank-account / facility names). They are not about the uploaded file, the
 *    user usually cannot act on them, and surfacing them made a fully
 *    successful 581-of-581 import read "Show 4 errors" (DCP-359). Not shown in
 *    Import History; still persisted and still logged for operators.
 *  - UNPREFIXED — notes about THIS file (a row rejected, a category that did
 *    not resolve, an ambiguous bank account the file actually references).
 *    Always shown, because the user can fix their file or their data.
 *
 * Shared rather than duplicated so a change to the marker is a compile-time
 * event on both sides. Dependency free — imported by the web app, by
 * `@dcp/tasks` and (transitively) by Deno Edge Functions.
 */

/**
 * Marker prefixing catalog-level, non-blocking importer notes.
 *
 * Written by the lookup builders in `@dcp/tasks` and read by the UI to keep
 * these notes out of the issue list. Changing the string changes both sides at
 * once — which is the whole point of it living here.
 */
export const IMPORT_WARNING_PREFIX = 'WARNING:'

/**
 * Is this stored issue a catalog-level warning rather than a real problem with
 * the uploaded file?
 *
 * Defensive about the argument on purpose: `errors` is JSONB written by several
 * releases, so a row may carry a non-string `message` (or none at all).
 *
 * @param message - `message` field of an entry in `file_imports.errors`
 * @returns true when the entry is a catalog warning and must not be listed
 */
export function isImportWarning(message: unknown): boolean {
  return typeof message === 'string' && message.startsWith(IMPORT_WARNING_PREFIX)
}
